/**
 * One spoken turn, over the metered stage socket.
 *
 * Everything here rides `StageSocketClient` — the same socket that
 * already carries typed notes and cues. That is the whole architectural
 * point: the server bills a spoken turn's three legs (`stt_second`, LLM
 * tokens, `tts_second`) under one hold and one settle, keyed to this
 * connection. A second socket to a dedicated speech endpoint would move
 * the audio off the meter, which is how the spend controls get bypassed
 * without anyone deciding to bypass them.
 *
 * Outbound: `client.human-audio` carrying base64 PCM.
 * Inbound:  `server.transcript` (what was heard), then `server.note`
 *           partials, then a terminal `server.note` whose `audio_b64` is
 *           the spoken reply.
 *
 * On telling failures apart: a refused spoken turn and a spent balance
 * arrive as different frames and mean opposite things to a visitor. One
 * says "try again, shorter" and the other says "come back tomorrow", so
 * `costCap.handleCapFrame` keeps first refusal on every inbound frame and
 * this module never touches the exhaustion codes. The banner ranks in
 * `bannerPrecedence` finish the job: audio failures sit at 60/40 and
 * cannot displace a cap banner at 100.
 */

import type { StageMessage } from '@/api';
import { newUlid } from '@/api';
import { showBanner } from '@/voice/bannerPrecedence';
import { makeAudioRejectedBanner } from '@/voice/fallbacks/audioRejected';
import { makeSttWsFailBanner } from '@/voice/fallbacks/sttWsFail';
import { makeTtsFailBanner } from '@/voice/fallbacks/ttsFail';
import { MAX_UTTERANCE_BYTES, pcmToBase64 } from '@/voice/pcmCapture';
import type { VoiceStoreSlice } from '@/voice/types';

/** Server `error_code` for a socket with no priced audio backend. */
export const ERROR_CODE_VOICE_UNAVAILABLE = 'voice_unavailable';
/** Server `error_code` for a frame the gateway could not parse. */
export const ERROR_CODE_VALIDATION_FAILED = 'validation_failed';

/** What happened to an attempted utterance. */
export type SendOutcome = 'sent' | 'empty' | 'too-long';

export interface SocketLike {
  send(msg: StageMessage): void;
}

export interface AudioSink {
  playBase64(b64: string): Promise<void>;
  cancel(): void;
}

export interface VoiceTurnDeps {
  readonly socket: SocketLike | null;
  readonly store: VoiceStoreSlice;
  readonly player: AudioSink;
  /** Append the recognizer's reading of the visitor to the take log. */
  readonly onTranscript: (text: string, id: string, ts: string) => void;
  /** Re-entry point offered by every banner this module raises. */
  readonly onRetry: () => void;
}

/**
 * Send one captured utterance.
 *
 * An empty buffer is not sent, and that covers both a visitor who tapped
 * the button twice and one whose capture path is dead — `PcmRecorder.stop`
 * already collapses digital silence to zero samples. Sending either would
 * buy a transcription of nothing at the going rate.
 */
export function sendUtterance(
  pcm: Int16Array,
  deps: VoiceTurnDeps,
  voiceId?: string,
): SendOutcome {
  if (pcm.length === 0) return 'empty';
  if (pcm.byteLength > MAX_UTTERANCE_BYTES) return 'too-long';
  const socket = deps.socket;
  if (socket === null) return 'empty';

  socket.send({
    id: newUlid(),
    ts: new Date().toISOString(),
    kind: 'client.human-audio',
    audio_b64: pcmToBase64(pcm),
    ...(voiceId !== undefined ? { voice_id: voiceId } : {}),
  });
  return 'sent';
}

/**
 * Ask a voice to stop talking so the visitor can.
 *
 * Local playback is cut immediately rather than waiting for the server to
 * agree, because the visitor has already started speaking and the only
 * thing that makes barge-in feel like barge-in is silence arriving now.
 * The gateway currently accepts and no-ops the frame; sending it anyway
 * means the client half is done when the server half lands.
 */
export function interrupt(deps: VoiceTurnDeps, voiceId: string): void {
  deps.player.cancel();
  deps.socket?.send({
    id: newUlid(),
    ts: new Date().toISOString(),
    kind: 'client.interrupt',
    voice_id: voiceId,
  });
}

/**
 * Route one inbound frame's voice concerns. Returns true when the frame
 * was a voice frame, so the caller can skip its own handling.
 *
 * `voice_unavailable` deliberately does not move `voiceMode` to
 * `fallen-back` or tear anything down. It is non-fatal by the server's
 * explicit design — the socket still serves typed notes, and the cause is
 * as often "that clip was too long" as "this deployment has no speech
 * provider". Latching the session into text-only would turn a retryable
 * mistake into a dead microphone for the rest of the visit.
 */
export function handleVoiceFrame(msg: StageMessage, deps: VoiceTurnDeps): boolean {
  if (msg.kind === 'server.transcript') {
    deps.onTranscript(msg.text, msg.id, msg.ts);
    return true;
  }

  if (msg.kind === 'server.note') {
    const b64 = msg.audio_b64;
    if (b64 === undefined || b64 === null || b64.length === 0) return false;
    void deps.player.playBase64(b64).catch(() => {
      // The words are already on screen; only the speaking failed, which
      // is why this banner ranks below every input-side failure.
      showBanner(deps.store, makeTtsFailBanner(deps.onRetry));
    });
    return false; // the note is still a note — the caller logs its text
  }

  if (msg.kind === 'server.error') {
    switch (msg.error.error) {
      case ERROR_CODE_VOICE_UNAVAILABLE:
        showBanner(deps.store, makeSttWsFailBanner(deps.onRetry));
        return true;
      case ERROR_CODE_VALIDATION_FAILED:
        showBanner(deps.store, makeAudioRejectedBanner(deps.onRetry));
        return true;
      default:
        return false;
    }
  }

  return false;
}
